import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { crossTenantStyles as styles, crossTenantStatusStyle as statusStyle } from './crossTenantStyles';
import { CrossTenantGroupedList } from './CrossTenantGroupedList';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Platform operator: cross-tenant Events view (2-pane; single-pane on mobile).
 *
 * Left list (event, tenant, date, venue, status, guests), right detail pane
 * (venue, metrics, enabled modules, onboarding %). Spans ALL tenants via
 * GET /platform/events — operator space only.
 */

interface EventRow {
  id: string;
  slug: string | null;
  name: string;
  date: string | null;
  status: string;
  venueName: string | null;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  guestCount: number;
  ticketCount: number;
}

interface Venue {
  id: string;
  name: string;
}

interface EventDetail {
  id: string;
  slug: string | null;
  name: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  status: string;
  venueId: string | null;
  venueName: string | null;
  roomName: string | null;
  mealInfo: string | null;
  dressCode: string | null;
  transportInfo: string | null;
  parkingInfo: string | null;
  presenterInstructions: string | null;
  affiliationLabel: string | null;
  venueLocation: string | null;
  venueCapacity: number | null;
  maxCapacity: number;
  tenant: { id: string; name: string; slug: string } | null;
  guestCount: number;
  ticketCount: number;
  orderCount: number;
  tableCount: number;
  zoneCount: number;
  seatCount: number;
  assignedSeats: number;
  revenue: number;
  enabledModules: string[];
  onboardingPercent: number;
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'Date TBD';
  return iso.split('T')[0];
}
/** HH:MM from an ISO-ish string (stored Eastern-as-ISO), for <input type=time>. */
function timePart(iso: string | null): string {
  if (!iso) return '';
  return iso.includes('T') ? iso.split('T')[1].slice(0, 5) : iso.slice(0, 5);
}

interface EventEditForm {
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueId: string;
  maxCapacity: string;
  roomName: string;
  mealInfo: string;
  dressCode: string;
  transportInfo: string;
  parkingInfo: string;
  presenterInstructions: string;
  affiliationLabel: string;
}

function toForm(d: EventDetail): EventEditForm {
  return {
    name: d.name ?? '',
    date: d.date ? d.date.split('T')[0] : '',
    startTime: timePart(d.startTime),
    endTime: timePart(d.endTime),
    venueId: d.venueId ?? '',
    maxCapacity: String(d.maxCapacity ?? ''),
    roomName: d.roomName ?? '',
    mealInfo: d.mealInfo ?? '',
    dressCode: d.dressCode ?? '',
    transportInfo: d.transportInfo ?? '',
    parkingInfo: d.parkingInfo ?? '',
    presenterInstructions: d.presenterInstructions ?? '',
    affiliationLabel: d.affiliationLabel ?? '',
  };
}
function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PlatformEventsPage() {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EventEditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const isMobile = useIsMobile();
  // On mobile we show one pane at a time; selecting an item flips to the detail.
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  useEffect(() => {
    apiClient<Venue[]>('/platform/venues')
      .then((v) => setVenues(v || []))
      .catch(() => setVenues([]));
  }, []);

  function startEdit() {
    if (!detail) return;
    setForm(toForm(detail));
    setSaveError('');
    setEditing(true);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !detail) return;
    setSaving(true);
    setSaveError('');
    // Dates/times stored as entered (Eastern-as-ISO), matching the hub.
    const body: Record<string, unknown> = {
      name: form.name,
      date: form.date,
      venueId: form.venueId || undefined,
      roomName: form.roomName || null,
      maxCapacity: form.maxCapacity ? parseInt(form.maxCapacity, 10) : undefined,
      mealInfo: form.mealInfo || null,
      dressCode: form.dressCode || null,
      transportInfo: form.transportInfo || null,
      parkingInfo: form.parkingInfo || null,
      presenterInstructions: form.presenterInstructions || null,
      affiliationLabel: form.affiliationLabel || undefined,
    };
    if (form.date && form.startTime) body.startTime = `${form.date}T${form.startTime}:00`;
    if (form.date && form.endTime) body.endTime = `${form.date}T${form.endTime}:00`;
    try {
      const updated = await apiClient<EventDetail>(`/platform/events/${detail.id}`, {
        method: 'PATCH',
        body,
      });
      setDetail(updated);
      // Reflect name/date/venue changes in the left list without a full reload.
      setEvents((prev) => prev.map((r) =>
        r.id === updated.id
          ? { ...r, name: updated.name, date: updated.date, venueName: updated.venueName, status: updated.status }
          : r,
      ));
      setEditing(false);
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient<EventRow[]>('/platform/events');
        if (!cancelled) {
          setEvents(d);
          // Honor a deep-link slug (or id) from the URL; else select the first.
          const target = routeSlug
            ? d.find((e) => e.slug === routeSlug || e.id === routeSlug)
            : undefined;
          if (target) setSelectedId(target.id);
          else if (d.length > 0) setSelectedId(d[0].id);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setEditing(false);
    setSaveError('');
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const d = await apiClient<EventDetail>(`/platform/events/${selectedId}`);
        if (!cancelled) setDetail(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const filtered = events.filter((e) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.tenantName.toLowerCase().includes(q) ||
      (e.venueName || '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Events</h1>
          <p style={styles.sub}>Every event across all organizations on the platform.</p>
        </div>
        <input
          style={styles.search}
          placeholder="Search event, tenant, venue…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading ? (
        <div style={styles.muted}>Loading events…</div>
      ) : (
        <div style={isMobile ? styles.onePane : styles.twoPane}>
          {/* Left: org-grouped collapsible list (hidden on mobile when viewing detail) */}
          {(!isMobile || mobileView === 'list') && (
          <div style={styles.listPane}>
            <CrossTenantGroupedList
              rows={filtered}
              selectedId={selectedId}
              onSelect={(e) => { setSelectedId(e.id); setMobileView('detail'); navigate(`/platform/events/${e.slug || e.id}`, { replace: true }); }}
              renderTitle={(e) => e.name}
              renderMeta={(e) => `📅 ${fmtDate(e.date)} · 📍 ${e.venueName || 'TBD'}`}
              renderTrailing={(e) => `${e.guestCount} 👥`}
              statusOf={(e) => e.status}
              itemNoun="event"
              emptyLabel="No events match."
              forceExpand={!!query.trim()}
            />
          </div>
          )}

          {/* Right: detail (hidden on mobile when viewing the list) */}
          {(!isMobile || mobileView === 'detail') && (
          <div style={styles.detailPane}>
            {isMobile && (
              <button type="button" style={styles.mobileBack} onClick={() => setMobileView('list')}>
                ← Back to list
              </button>
            )}
            {detailLoading ? (
              <div style={styles.muted}>Loading…</div>
            ) : !detail ? (
              <div style={styles.detailEmpty}>Select an event to see its details.</div>
            ) : (
              <>
                <div style={styles.detailHead}>
                  <div style={styles.detailTitleStack}>
                    {detail.tenant && (
                      <span style={styles.detailOrg}>🏢 {detail.tenant.name}</span>
                    )}
                    <h2 style={styles.detailName}>{detail.name}</h2>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={detail.enabledModules.includes('ticketing') ? styles.badgeTicketed : styles.badgeFree}>
                      {detail.enabledModules.includes('ticketing') ? '🎟️ Ticketed' : '🎉 Free'}
                    </span>
                    <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
                    {!editing && (
                      <button style={styles.editBtn} onClick={startEdit} title="Edit event details">
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                </div>

                {editing && form ? (
                  <form onSubmit={saveEdit} style={styles.editForm}>
                    {saveError && <div style={styles.error}>{saveError}</div>}
                    <label style={styles.fLabel}>Name
                      <input style={styles.fInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Date
                      <input type="date" style={styles.fInput} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </label>
                    <div style={styles.fRow}>
                      <label style={{ ...styles.fLabel, flex: 1 }}>Start
                        <input type="time" style={styles.fInput} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                      </label>
                      <label style={{ ...styles.fLabel, flex: 1 }}>End
                        <input type="time" style={styles.fInput} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                      </label>
                    </div>
                    <label style={styles.fLabel}>Venue
                      <select style={styles.fInput} value={form.venueId} onChange={(e) => setForm({ ...form, venueId: e.target.value })}>
                        <option value="">Select a venue…</option>
                        {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    </label>
                    <div style={styles.fRow}>
                      <label style={{ ...styles.fLabel, flex: 1 }}>Room
                        <input style={styles.fInput} value={form.roomName} onChange={(e) => setForm({ ...form, roomName: e.target.value })} />
                      </label>
                      <label style={{ ...styles.fLabel, flex: 1 }}>Max capacity
                        <input type="number" min="1" max="9999" style={styles.fInput} value={form.maxCapacity} onChange={(e) => setForm({ ...form, maxCapacity: e.target.value })} />
                      </label>
                    </div>
                    <label style={styles.fLabel}>Affiliation label
                      <input style={styles.fInput} value={form.affiliationLabel} onChange={(e) => setForm({ ...form, affiliationLabel: e.target.value })} placeholder="e.g. Chapter, Company, Family" />
                    </label>
                    <label style={styles.fLabel}>Meal info
                      <input style={styles.fInput} value={form.mealInfo} onChange={(e) => setForm({ ...form, mealInfo: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Dress code
                      <input style={styles.fInput} value={form.dressCode} onChange={(e) => setForm({ ...form, dressCode: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Transport info
                      <input style={styles.fInput} value={form.transportInfo} onChange={(e) => setForm({ ...form, transportInfo: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Parking info
                      <input style={styles.fInput} value={form.parkingInfo} onChange={(e) => setForm({ ...form, parkingInfo: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Presenter instructions
                      <textarea style={{ ...styles.fInput, minHeight: 60, resize: 'vertical' as const }} value={form.presenterInstructions} onChange={(e) => setForm({ ...form, presenterInstructions: e.target.value })} />
                    </label>
                    <div style={styles.fActions}>
                      <button type="button" style={styles.cancelBtn} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                      <button type="submit" style={styles.saveBtn} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                    </div>
                  </form>
                ) : (
                <>
                <div style={styles.ownerRow}>
                  🏢 <strong>{detail.tenant?.name ?? '(no organization)'}</strong>
                  {detail.tenant?.slug && <span style={styles.slug}>/{detail.tenant.slug}</span>}
                </div>
                <div style={styles.ownerRow}>
                  📅 {fmtDate(detail.date)} · 📍 {detail.venueName || 'TBD'}
                  {detail.roomName && <span> · 🚪 {detail.roomName}</span>}
                  {detail.venueLocation && <span style={styles.slug}> ({detail.venueLocation})</span>}
                </div>

                {/* Onboarding progress */}
                <div style={styles.obWrap}>
                  <div style={styles.obHead}>
                    <span>Onboarding</span>
                    <span style={{ fontWeight: 700, color: detail.onboardingPercent === 100 ? '#166534' : '#92400e' }}>
                      {detail.onboardingPercent}%
                    </span>
                  </div>
                  <div style={styles.obBar}>
                    <div style={{ ...styles.obFill, width: `${detail.onboardingPercent}%`, background: detail.onboardingPercent === 100 ? '#16a34a' : '#f59e0b' }} />
                  </div>
                </div>

                <div style={styles.infoGrid}>
                  <Info label="Guests" value={String(detail.guestCount)} />
                  <Info label="Tickets" value={String(detail.ticketCount)} />
                  <Info label="Orders" value={String(detail.orderCount)} />
                  <Info label="Revenue" value={money(detail.revenue)} />
                  <Info label="Zones / Tables" value={`${detail.zoneCount} / ${detail.tableCount}`} />
                  <Info label="Seats (assigned)" value={`${detail.seatCount} (${detail.assignedSeats})`} />
                </div>

                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Enabled modules</h3>
                  {detail.enabledModules.length === 0 ? (
                    <div style={styles.muted}>None enabled.</div>
                  ) : (
                    <div style={styles.moduleWrap}>
                      {detail.enabledModules.map((m) => (
                        <span key={m} style={styles.moduleChip}>{m}</span>
                      ))}
                    </div>
                  )}
                </div>
                </>
                )}
              </>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.info}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value}</div>
    </div>
  );
}

